import { type BlockedRequirement, decideUploadCompletion, type OutboxState } from "../outbox-state";
import { type OutboxStore } from "../store-contract";
import {
  type AttachmentTarget,
  type ChangeRequestAttachmentRequest,
  type ChangeRequestReceipt,
  type ChangeRequestSetCreate,
  type DraftSidecar,
  type EnqueueAttachmentInput,
  type EvidenceKind,
  type FormSubmissionCreate,
  type IntakeCandidate,
  type OutboxAssignmentRow,
  type OutboxAttachmentRow,
  type OutboxAttachmentState,
  type OutboxChangeRequestSetRow,
  type OutboxChangeRequestSetState,
  type OutboxSubmissionRow,
  type OutboxSupplementRow,
  sidecarEvidenceOwners,
  type SyncStack,
  type WorkAssignmentStatus,
} from "../types";
import { compareStrings, getAllRows, getRow, runWrite, txDone, updateRow } from "./db";

// The append-only regulatory outbox over the pure machine in outbox-state.ts,
// transcribed from the device store's SQL. Every row snapshots which stack
// (and which org) it replays against — the CURRENT app mode is irrelevant at
// replay time (the org-switch hazard). UUIDv7 primary keys sort
// chronologically, which is what makes FIFO replay a plain primary-key walk.

// Exported for the proposal cure store beside this factory, which rewrites the
// same rows outside the storage adapter.
export interface SubmissionRow {
  client_submission_id: string;
  assignment_id: string;
  org_id: string;
  stack: SyncStack;
  payload_json: string;
  state: OutboxState;
  server_submission_id: string | null;
  final_status: string | null;
  attempts: number;
  last_error: string | null;
  blocked_requirements: BlockedRequirement[] | null;
  server_change_request_set_id: string | null;
  created_at: number;
  updated_at: number;
}

function toSubmissionRow(r: SubmissionRow): OutboxSubmissionRow {
  return {
    clientSubmissionId: r.client_submission_id,
    assignmentId: r.assignment_id,
    orgId: r.org_id,
    stack: r.stack,
    payloadJson: r.payload_json,
    state: r.state,
    serverSubmissionId: r.server_submission_id,
    finalStatus: r.final_status,
    attempts: r.attempts,
    lastError: r.last_error,
    blockedRequirements: r.blocked_requirements ?? null,
    serverChangeRequestSetId: r.server_change_request_set_id,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

// A standalone set's row carries no client_submission_id and a form file's no
// client_set_id: IndexedDB leaves a null-keyed record out of that index, so
// each owner index holds exactly its own rows.
interface AttachmentRow {
  client_attachment_id: string;
  target: AttachmentTarget;
  client_submission_id: string | null;
  client_set_id: string | null;
  link_id: string;
  kind: EvidenceKind;
  file_uri: string;
  content_type: string;
  request: ChangeRequestAttachmentRequest | null;
  file_name: string;
  staged_at: string;
  state: OutboxAttachmentState;
  attempts: number;
  last_error: string | null;
}

function toAttachmentRow(r: AttachmentRow): OutboxAttachmentRow {
  return {
    clientAttachmentId: r.client_attachment_id,
    target: r.target,
    clientSubmissionId: r.client_submission_id,
    clientSetId: r.client_set_id,
    linkId: r.link_id,
    kind: r.kind,
    locator: r.file_uri,
    contentType: r.content_type,
    request: r.request,
    fileName: r.file_name,
    stagedAt: r.staged_at,
    state: r.state,
    attempts: r.attempts,
    lastError: r.last_error,
  };
}

// One attachment row as an enqueue writes it: `staged`, owned by exactly one
// of the submission or the set.
function newAttachmentRow(
  a: EnqueueAttachmentInput,
  owner: { clientSubmissionId: string } | { clientSetId: string },
): AttachmentRow {
  return {
    client_attachment_id: a.clientAttachmentId,
    target: a.request === undefined ? "form" : "change_request_set",
    client_submission_id: "clientSubmissionId" in owner ? owner.clientSubmissionId : null,
    client_set_id: "clientSetId" in owner ? owner.clientSetId : null,
    link_id: a.linkId,
    kind: a.kind,
    file_uri: a.locator,
    content_type: a.contentType,
    request: a.request ?? null,
    file_name: a.fileName,
    staged_at: a.stagedAt,
    state: "staged",
    attempts: 0,
    last_error: null,
  };
}

interface ChangeRequestSetRow {
  client_set_id: string;
  body_json: string;
  state: OutboxChangeRequestSetState;
  receipt: ChangeRequestReceipt | null;
  candidates: IntakeCandidate[] | null;
  last_check_failed_at: number | null;
  evidence_failures: BlockedRequirement[] | null;
  attempts: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

// body_json was written as JSON.stringify of a ChangeRequestSetCreate.
function parseBody(row: ChangeRequestSetRow): ChangeRequestSetCreate {
  return JSON.parse(row.body_json) as ChangeRequestSetCreate;
}

function toChangeRequestSetRow(r: ChangeRequestSetRow): OutboxChangeRequestSetRow {
  return {
    clientSetId: r.client_set_id,
    bodyJson: r.body_json,
    state: r.state,
    receipt: r.receipt,
    candidates: r.candidates,
    lastCheckFailedAt: r.last_check_failed_at,
    blockedRequirements: r.evidence_failures,
    attempts: r.attempts,
    lastError: r.last_error,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

interface TransitionRow {
  assignment_id: string;
  org_id: string;
  stack: SyncStack;
  to_status: WorkAssignmentStatus;
  state: "queued" | "synced";
  created_at: number;
}

interface AssignmentRow {
  assignment_id: string;
  org_id: string;
  stack: SyncStack;
  payload_json: string;
  state: OutboxAssignmentRow["state"];
  attempts: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

function toAssignmentRow(r: AssignmentRow): OutboxAssignmentRow {
  return {
    assignmentId: r.assignment_id,
    orgId: r.org_id,
    stack: r.stack,
    payloadJson: r.payload_json,
    state: r.state,
    attempts: r.attempts,
    lastError: r.last_error,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

interface SupplementRow {
  client_submission_id: string;
  parent_client_submission_id: string;
  company_id: string;
  template_version_id: string;
  org_id: string;
  stack: SyncStack;
  responses_json: string;
  state: OutboxSupplementRow["state"];
  attempts: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

function toSupplementRow(r: SupplementRow): OutboxSupplementRow {
  return {
    clientSubmissionId: r.client_submission_id,
    parentClientSubmissionId: r.parent_client_submission_id,
    companyId: r.company_id,
    templateVersionId: r.template_version_id,
    orgId: r.org_id,
    stack: r.stack,
    responsesJson: r.responses_json,
    state: r.state,
    attempts: r.attempts,
    lastError: r.last_error,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

// The one column the enqueue lock flips on the draft row; the full row shape
// belongs to the draft store (the PendingAssignmentRow precedent).
interface LockableDraftRow {
  locked: boolean;
}

// `state IN (…)` predicates, one per device-store query. A blocked row is
// counted (the work is not done — it waits on a cure) but never pushed: the
// next POST would draw the same refusal until `retryBlocked` releases it.
const PUSH_PENDING_STATES = new Set<OutboxState>(["finalized", "attachments_pending"]);
const PENDING_SUBMISSION_STATES = new Set<OutboxState>([
  "finalized",
  "sending",
  "attachments_pending",
  "blocked",
]);
const ACKED_STATES = new Set<OutboxState>(["accepted", "needs_review"]);
const PENDING_WALKUP_STATES = new Set<AssignmentRow["state"]>(["queued", "sending", "error"]);

// Walks the primary-key cursor — client ids are UUIDv7, so key order IS
// chronological order — and settles on the first row matching the state
// predicate rather than loading the whole store: the
// `… ORDER BY <key> ASC LIMIT 1` twin.
function firstRowWhere<T>(
  store: IDBObjectStore,
  predicate: (row: T) => boolean,
): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor === null) {
        resolve(null);
        return;
      }
      const row: T = cursor.value;
      if (predicate(row)) {
        resolve(row);
        return;
      }
      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb_request_failed"));
    };
  });
}

// Cursor-walks `store` mutating every row the predicate matches — the
// multi-row `UPDATE … WHERE` twin, running inside the caller's transaction.
function updateRowsWhere<T>(
  store: IDBObjectStore,
  predicate: (row: T) => boolean,
  mutate: (row: T) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor === null) {
        resolve();
        return;
      }
      const row: T = cursor.value;
      if (predicate(row)) {
        mutate(row);
        cursor.update(row);
      }
      cursor.continue();
    };
    request.onerror = () => {
      reject(request.error ?? new Error("indexeddb_request_failed"));
    };
  });
}

// Deletes every row of an owner-indexed store (draft attachments, evidence)
// filed under `owner`, inside the caller's transaction.
async function deleteByOwner(store: IDBObjectStore, owner: string): Promise<void> {
  const rows = await getAllRows<{ client_attachment_id: string }>(
    store.index("assignment_id"),
    owner,
  );
  for (const r of rows) {
    store.delete(r.client_attachment_id);
  }
}

// Reference integrity at commit, inside the caller's transaction: every
// attachment whose locator THIS adapter minted (locator = clientAttachmentId,
// the evidence store's scheme) must still resolve to a staged evidence row.
// A locator minted by another storage domain is an address this transaction
// cannot dereference; readEvidencePart still rejects it loudly at upload.
async function assertEvidenceStaged(
  tx: IDBTransaction,
  attachments: readonly EnqueueAttachmentInput[],
): Promise<void> {
  const evidence = tx.objectStore("evidence");
  for (const a of attachments) {
    if (a.locator !== a.clientAttachmentId) continue;
    if ((await getRow(evidence, a.clientAttachmentId)) === null) {
      throw new Error(`evidence_unstaged:${a.clientAttachmentId}`);
    }
  }
}

export function createOutboxStore(db: IDBDatabase): OutboxStore {
  const readStore = (name: string): IDBObjectStore =>
    db.transaction(name, "readonly").objectStore(name);
  const updateSetRow = (
    clientSetId: string,
    mutate: (row: ChangeRequestSetRow) => boolean,
  ): Promise<void> => updateRow(db, "outbox_change_request_sets", clientSetId, mutate);

  return {
    // One readwrite transaction over the queue, the draft AND the evidence
    // store: the outbox row lands `finalized`, every attachment gets an
    // `outbox_attachments` row, and the draft is locked — all or nothing.
    // A self-minted locator with no staged evidence row aborts the enqueue
    // (assertEvidenceStaged) — a queued document must never outlive its
    // bytes. `add` is the INSERT twin: a duplicate clientSubmissionId aborts
    // the transaction, rolling back every write. A quota abort maps to
    // FieldSyncQuotaError("enqueue") — the atomicity leaves the draft intact
    // and unlocked, so the tester retries after freeing space.
    async enqueueSubmission(input, now = Date.now()) {
      await runWrite(
        db,
        ["outbox_submissions", "outbox_attachments", "drafts", "evidence"],
        "enqueue",
        async (tx) => {
          await assertEvidenceStaged(tx, input.attachments);
          tx.objectStore("outbox_submissions").add({
            client_submission_id: input.clientSubmissionId,
            assignment_id: input.assignmentId,
            org_id: input.orgId,
            stack: input.stack,
            payload_json: JSON.stringify(input.payload),
            state: "finalized",
            server_submission_id: null,
            final_status: null,
            attempts: 0,
            last_error: null,
            blocked_requirements: null,
            server_change_request_set_id: null,
            created_at: now,
            updated_at: now,
          } satisfies SubmissionRow);
          const attachments = tx.objectStore("outbox_attachments");
          for (const a of input.attachments) {
            attachments.add(newAttachmentRow(a, { clientSubmissionId: input.clientSubmissionId }));
          }
          const drafts = tx.objectStore("drafts");
          const draft = await getRow<LockableDraftRow>(drafts, input.assignmentId);
          if (draft !== null) {
            draft.locked = true;
            drafts.put(draft);
          }
        },
      );
    },

    // Oldest push-pending row first: `finalized` rows await their POST,
    // `attachments_pending` rows await their remaining uploads.
    async nextPending() {
      const row = await firstRowWhere<SubmissionRow>(readStore("outbox_submissions"), (r) =>
        PUSH_PENDING_STATES.has(r.state),
      );
      return row === null ? null : toSubmissionRow(row);
    },

    async markSending(clientSubmissionId, now = Date.now()) {
      await updateRow<SubmissionRow>(db, "outbox_submissions", clientSubmissionId, (row) => {
        if (row.state !== "finalized") return false;
        row.state = "sending";
        row.updated_at = now;
        return true;
      });
    },

    // Applies a decidePostOutcome result. null = retryable failure: increment
    // attempts and return the row to `finalized` for the next sync trigger —
    // last_error stays UNTOUCHED, exactly the device store's column list.
    async applyPostOutcome(clientSubmissionId, outcome, now = Date.now()) {
      if (outcome === null) {
        await updateRow<SubmissionRow>(db, "outbox_submissions", clientSubmissionId, (row) => {
          row.state = "finalized";
          row.attempts += 1;
          row.updated_at = now;
          return true;
        });
        return;
      }
      const finalStatus =
        outcome.next === "accepted" || outcome.next === "needs_review" ? outcome.next : null;
      // The refusal's requirements ride the row only while it is blocked: any
      // other outcome is the utility's newer word on the same document, so a
      // stale cure list would outlive the refusal it described.
      const blockedRequirements =
        outcome.next === "blocked" ? (outcome.blockedRequirements ?? null) : null;
      await updateRow<SubmissionRow>(db, "outbox_submissions", clientSubmissionId, (row) => {
        row.state = outcome.next;
        row.server_submission_id = outcome.serverSubmissionId ?? null;
        row.server_change_request_set_id = outcome.serverChangeRequestSetId ?? null;
        row.final_status = finalStatus;
        row.last_error = outcome.lastError ?? null;
        row.blocked_requirements = blockedRequirements;
        row.updated_at = now;
        return true;
      });
    },

    // The way back from blocked, once the tester (or the utility) cures what
    // the refusal named: the document returns to the queue exactly as it was
    // captured — same attempts, same attachment rows, same staged bytes — and
    // loses only the cure list it no longer needs. Any other state is a
    // no-op, so a retry racing an accept cannot resurrect a settled row.
    async retryBlocked(clientSubmissionId, now = Date.now()) {
      await updateRow<SubmissionRow>(db, "outbox_submissions", clientSubmissionId, (row) => {
        if (row.state !== "blocked") return false;
        row.state = "finalized";
        row.blocked_requirements = null;
        row.updated_at = now;
        return true;
      });
    },

    // One transaction: the payload's two request members swap under the same
    // client ids, the cure's attachment rows land, and the row returns to the
    // queue — or none of it, when the row is not blocked or a self-minted
    // locator has no staged bytes. Parse-then-stringify of a JSON.stringify
    // product is the identity, so every other payload member stays
    // byte-identical.
    async amendBlockedSubmission(
      clientSubmissionId,
      requestMembers,
      addedAttachments,
      now = Date.now(),
    ) {
      await runWrite(
        db,
        ["outbox_submissions", "outbox_attachments", "evidence"],
        "enqueue",
        async (tx) => {
          const submissions = tx.objectStore("outbox_submissions");
          const row = await getRow<SubmissionRow>(submissions, clientSubmissionId);
          if (row === null || row.state !== "blocked") return;
          await assertEvidenceStaged(tx, addedAttachments);
          // payload_json was written as JSON.stringify of a FormSubmissionCreate.
          const payload = JSON.parse(row.payload_json) as FormSubmissionCreate;
          payload.device_of_record = requestMembers.device_of_record;
          payload.change_requests = requestMembers.change_requests;
          row.payload_json = JSON.stringify(payload);
          row.state = "finalized";
          row.blocked_requirements = null;
          row.updated_at = now;
          submissions.put(row);
          const attachments = tx.objectStore("outbox_attachments");
          for (const a of addedAttachments) {
            attachments.add(newAttachmentRow(a, { clientSubmissionId }));
          }
        },
      );
    },

    async listAttachmentsForSubmission(clientSubmissionId) {
      const rows = await getAllRows<AttachmentRow>(
        readStore("outbox_attachments").index("client_submission_id"),
        clientSubmissionId,
      );
      // ORDER BY client_attachment_id ASC — UUIDv7 mint order.
      rows.sort((x, y) => compareStrings(x.client_attachment_id, y.client_attachment_id));
      return rows.map(toAttachmentRow);
    },

    async markAttachmentSending(clientAttachmentId) {
      await updateRow<AttachmentRow>(db, "outbox_attachments", clientAttachmentId, (row) => {
        if (row.state !== "staged") return false;
        row.state = "sending";
        return true;
      });
    },

    // Marks one upload acked. Every upload response carries the submission's
    // post-gate truth (`submission_status` + `attachments_complete`); when the
    // set completes, the parent row transitions via decideUploadCompletion —
    // no follow-up GET exists on the field surface by design.
    async markAttachmentUploaded(
      clientAttachmentId,
      submissionStatus,
      attachmentsComplete,
      now = Date.now(),
    ) {
      const tx = db.transaction(["outbox_attachments", "outbox_submissions"], "readwrite");
      const attachments = tx.objectStore("outbox_attachments");
      const attachment = await getRow<AttachmentRow>(attachments, clientAttachmentId);
      if (attachment !== null) {
        attachment.state = "uploaded";
        attachments.put(attachment);
        if (attachmentsComplete && attachment.client_submission_id !== null) {
          const next = decideUploadCompletion(submissionStatus);
          const finalStatus = next === "error" ? null : submissionStatus;
          const submissions = tx.objectStore("outbox_submissions");
          const parent = await getRow<SubmissionRow>(submissions, attachment.client_submission_id);
          if (parent !== null) {
            parent.state = next;
            parent.final_status = finalStatus;
            parent.updated_at = now;
            submissions.put(parent);
          }
        }
      }
      await txDone(tx);
    },

    // Staged evidence files are deleted ONLY after the server confirmed the
    // set complete; this is the post-cleanup terminal hop. It also ends the
    // test's sidecar in the same transaction: the rows and bytes under every
    // owner the sidecar names go first, then the sidecar.
    async markCleanedUp(clientSubmissionId, now = Date.now()) {
      const tx = db.transaction(
        ["outbox_submissions", "draft_sidecars", "draft_attachments", "evidence"],
        "readwrite",
      );
      const submissions = tx.objectStore("outbox_submissions");
      const row = await getRow<SubmissionRow>(submissions, clientSubmissionId);
      if (row !== null && ACKED_STATES.has(row.state)) {
        row.state = "synced";
        row.updated_at = now;
        submissions.put(row);
        const sidecars = tx.objectStore("draft_sidecars");
        const sidecar = await getRow<{ key: string; sidecar_json: string }>(
          sidecars,
          row.assignment_id,
        );
        if (sidecar !== null) {
          // sidecar_json was written as JSON.stringify of a DraftSidecar.
          const owners = sidecarEvidenceOwners(JSON.parse(sidecar.sidecar_json) as DraftSidecar);
          for (const owner of owners) {
            await deleteByOwner(tx.objectStore("draft_attachments"), owner);
            await deleteByOwner(tx.objectStore("evidence"), owner);
          }
          sidecars.delete(row.assignment_id);
        }
      }
      await txDone(tx);
    },

    // At engine start, in-flight rows from an interrupted run revert to their
    // pre-flight states across all five queues in one transaction. Replay is
    // safe by UUIDv7 idempotency (a duplicate POST returns 200 with the
    // existing row; uploads are per-file idempotent). A set already in
    // `uploading_evidence` keeps its receipt and state: its uploads resume.
    async recoverInFlight(now = Date.now()) {
      const tx = db.transaction(
        [
          "outbox_submissions",
          "outbox_attachments",
          "outbox_supplements",
          "outbox_assignments",
          "outbox_change_request_sets",
        ],
        "readwrite",
      );
      const sending = (r: { state: string }): boolean => r.state === "sending";
      await updateRowsWhere<SubmissionRow>(tx.objectStore("outbox_submissions"), sending, (r) => {
        r.state = "finalized";
        r.updated_at = now;
      });
      await updateRowsWhere<AttachmentRow>(tx.objectStore("outbox_attachments"), sending, (r) => {
        r.state = "staged";
      });
      await updateRowsWhere<SupplementRow>(tx.objectStore("outbox_supplements"), sending, (r) => {
        r.state = "finalized";
        r.updated_at = now;
      });
      await updateRowsWhere<AssignmentRow>(tx.objectStore("outbox_assignments"), sending, (r) => {
        r.state = "queued";
        r.updated_at = now;
      });
      await updateRowsWhere<ChangeRequestSetRow>(
        tx.objectStore("outbox_change_request_sets"),
        sending,
        (r) => {
          r.state = "queued";
          r.updated_at = now;
        },
      );
      await txDone(tx);
    },

    // The sync-bar count: not-yet-synced submission rows — the blocked park
    // included, because a refused document is work the tester still owes —
    // PLUS not-yet-synced walk-up assignment creates (queued / sending
    // in-flight, or error parked: an errored create is a needs-attention item
    // that stays surfaced).
    async pendingCount() {
      const submissions = await getAllRows<SubmissionRow>(readStore("outbox_submissions"));
      const assignments = await getAllRows<AssignmentRow>(readStore("outbox_assignments"));
      return (
        submissions.filter((r) => PENDING_SUBMISSION_STATES.has(r.state)).length +
        assignments.filter((r) => PENDING_WALKUP_STATES.has(r.state)).length
      );
    },

    async listForAssignment(assignmentId) {
      const rows = await getAllRows<SubmissionRow>(
        readStore("outbox_submissions").index("assignment_id"),
        assignmentId,
      );
      rows.sort((x, y) => compareStrings(x.client_submission_id, y.client_submission_id));
      return rows.map(toSubmissionRow);
    },

    // Single parent-submission row by its client uuid. The supplement push
    // reads the parent's serverSubmissionId off this row purely as a
    // replay-order gate (the parent must have recorded its server id before a
    // supplement that references it can be resolved server-side).
    async getOutboxSubmission(clientSubmissionId) {
      const row = await getRow<SubmissionRow>(readStore("outbox_submissions"), clientSubmissionId);
      return row === null ? null : toSubmissionRow(row);
    },

    // Rows the sync chips surface: everything not yet fully synced, including
    // the flagged terminals (`revoked`, `blocked`, `error`).
    async listVisible() {
      const rows = await getAllRows<SubmissionRow>(readStore("outbox_submissions"));
      const visible = rows.filter((r) => r.state !== "synced");
      visible.sort((x, y) => compareStrings(x.client_submission_id, y.client_submission_id));
      return visible.map(toSubmissionRow);
    },

    // One queued transition per assignment (primary key) — put is the
    // INSERT OR REPLACE twin, so a re-queue replaces the prior row with the
    // latest toStatus, stack and createdAt; the server FSM is forward-only and
    // no-ops stale replays.
    async queueTransition(input, now = Date.now()) {
      const tx = db.transaction("outbox_transitions", "readwrite");
      tx.objectStore("outbox_transitions").put({
        assignment_id: input.assignmentId,
        org_id: input.orgId,
        stack: input.stack,
        to_status: input.toStatus,
        state: "queued",
        created_at: now,
      } satisfies TransitionRow);
      await txDone(tx);
    },

    async nextQueuedTransition() {
      const rows = await getAllRows<TransitionRow>(readStore("outbox_transitions"));
      const queued = rows.filter((r) => r.state === "queued");
      // ORDER BY created_at ASC — this queue is keyed by assignment_id, so
      // FIFO walks the timestamp, not the key.
      queued.sort((x, y) => x.created_at - y.created_at);
      const row = queued[0];
      if (row === undefined) return null;
      return {
        assignmentId: row.assignment_id,
        orgId: row.org_id,
        stack: row.stack,
        toStatus: row.to_status,
        state: row.state,
        createdAt: row.created_at,
      };
    },

    async markTransitionSynced(assignmentId) {
      await updateRow<TransitionRow>(db, "outbox_transitions", assignmentId, (row) => {
        row.state = "synced";
        return true;
      });
    },

    // Enqueue a walk-up assignment create. `stack` is ALWAYS 'main' — walk-up
    // creation is org-mode-only — snapshotted here so an org switch (or mode
    // switch) before replay never re-routes the queued create.
    async enqueueWalkupAssignment(input, now = Date.now()) {
      const tx = db.transaction("outbox_assignments", "readwrite");
      tx.objectStore("outbox_assignments").add({
        assignment_id: input.assignmentId,
        org_id: input.orgId,
        stack: "main",
        payload_json: JSON.stringify(input.payload),
        state: "queued",
        attempts: 0,
        last_error: null,
        created_at: now,
        updated_at: now,
      } satisfies AssignmentRow);
      await txDone(tx);
    },

    // Every not-yet-synced walk-up assignment row (queued awaiting POST,
    // sending in-flight, or error parked) in FIFO order — the sync-UI count +
    // the walk-up pending section read this.
    async listPendingWalkupAssignments() {
      const rows = await getAllRows<AssignmentRow>(readStore("outbox_assignments"));
      const pending = rows.filter((r) => PENDING_WALKUP_STATES.has(r.state));
      pending.sort((x, y) => x.created_at - y.created_at);
      return pending.map(toAssignmentRow);
    },

    // Oldest queued create first; the push loop re-queries after each hop.
    async nextQueuedAssignment() {
      const rows = await getAllRows<AssignmentRow>(readStore("outbox_assignments"));
      const queued = rows.filter((r) => r.state === "queued");
      queued.sort((x, y) => x.created_at - y.created_at);
      const row = queued[0];
      return row === undefined ? null : toAssignmentRow(row);
    },

    async markAssignmentSending(assignmentId, now = Date.now()) {
      await updateRow<AssignmentRow>(db, "outbox_assignments", assignmentId, (row) => {
        if (row.state !== "queued") return false;
        row.state = "sending";
        row.updated_at = now;
        return true;
      });
    },

    async markAssignmentSynced(assignmentId, now = Date.now()) {
      await updateRow<AssignmentRow>(db, "outbox_assignments", assignmentId, (row) => {
        row.state = "synced";
        row.last_error = null;
        row.updated_at = now;
        return true;
      });
    },

    // Deterministic 4xx rejection: the create parks in error with the cause.
    // Its dependent submission/transition will itself fail deterministically
    // at POST and surface through the existing submission error state.
    async markAssignmentError(assignmentId, lastError, now = Date.now()) {
      await updateRow<AssignmentRow>(db, "outbox_assignments", assignmentId, (row) => {
        row.state = "error";
        row.last_error = lastError;
        row.updated_at = now;
        return true;
      });
    },

    // Retryable failure (network / 5xx / 429): return to queued, attempts++
    // for the next sync trigger — no last_error write, exactly the device
    // store's column list.
    async markAssignmentRetry(assignmentId, now = Date.now()) {
      await updateRow<AssignmentRow>(db, "outbox_assignments", assignmentId, (row) => {
        row.state = "queued";
        row.attempts += 1;
        row.updated_at = now;
        return true;
      });
    },

    async enqueueSupplement(input, now = Date.now()) {
      const tx = db.transaction("outbox_supplements", "readwrite");
      tx.objectStore("outbox_supplements").add({
        client_submission_id: input.clientSubmissionId,
        parent_client_submission_id: input.parentClientSubmissionId,
        company_id: input.companyId,
        template_version_id: input.templateVersionId,
        org_id: input.orgId,
        stack: input.stack,
        responses_json: JSON.stringify(input.responses),
        state: "finalized",
        attempts: 0,
        last_error: null,
        created_at: now,
        updated_at: now,
      } satisfies SupplementRow);
      await txDone(tx);
    },

    // Oldest pushable supplement first; UUIDv7 client ids sort
    // chronologically so FIFO replay is a plain key walk.
    async nextPendingSupplement() {
      const row = await firstRowWhere<SupplementRow>(
        readStore("outbox_supplements"),
        (r) => r.state === "finalized",
      );
      return row === null ? null : toSupplementRow(row);
    },

    // All pushable supplements in FIFO order. The drain walks this snapshot
    // so a supplement whose parent is not yet recordable can be skipped while
    // a newer supplement with a recorded parent still pushes — a single
    // oldest-row read would let one stuck parent block every healthy
    // supplement behind it.
    async listPendingSupplements() {
      const rows = await getAllRows<SupplementRow>(readStore("outbox_supplements"));
      const pending = rows.filter((r) => r.state === "finalized");
      pending.sort((x, y) => compareStrings(x.client_submission_id, y.client_submission_id));
      return pending.map(toSupplementRow);
    },

    async markSupplementSending(clientSubmissionId, now = Date.now()) {
      await updateRow<SupplementRow>(db, "outbox_supplements", clientSubmissionId, (row) => {
        if (row.state !== "finalized") return false;
        row.state = "sending";
        row.updated_at = now;
        return true;
      });
    },

    async markSupplementSynced(clientSubmissionId, now = Date.now()) {
      await updateRow<SupplementRow>(db, "outbox_supplements", clientSubmissionId, (row) => {
        row.state = "synced";
        row.last_error = null;
        row.updated_at = now;
        return true;
      });
    },

    // Deterministic 4xx rejection: the supplement parks in error with the
    // cause.
    async markSupplementError(clientSubmissionId, lastError, now = Date.now()) {
      await updateRow<SupplementRow>(db, "outbox_supplements", clientSubmissionId, (row) => {
        row.state = "error";
        row.last_error = lastError;
        row.updated_at = now;
        return true;
      });
    },

    // Retryable failure (network / 5xx): return to finalized, attempts++,
    // capture the cause for the next sync trigger.
    async markSupplementRetry(clientSubmissionId, lastError, now = Date.now()) {
      await updateRow<SupplementRow>(db, "outbox_supplements", clientSubmissionId, (row) => {
        row.state = "finalized";
        row.attempts += 1;
        row.last_error = lastError;
        row.updated_at = now;
        return true;
      });
    },

    async getOutboxSupplement(clientSubmissionId) {
      const row = await getRow<SupplementRow>(readStore("outbox_supplements"), clientSubmissionId);
      return row === null ? null : toSupplementRow(row);
    },

    // The set row and every attachment row land together, or — a duplicate
    // key, a self-minted locator with no staged bytes — nothing does.
    async enqueueChangeRequestSet(input, now = Date.now()) {
      await runWrite(
        db,
        ["outbox_change_request_sets", "outbox_attachments", "evidence"],
        "enqueue",
        async (tx) => {
          await assertEvidenceStaged(tx, input.attachments);
          tx.objectStore("outbox_change_request_sets").add({
            client_set_id: input.clientSetId,
            body_json: JSON.stringify(input.body),
            state: "queued",
            receipt: null,
            candidates: null,
            last_check_failed_at: null,
            evidence_failures: null,
            attempts: 0,
            last_error: null,
            created_at: now,
            updated_at: now,
          } satisfies ChangeRequestSetRow);
          const attachments = tx.objectStore("outbox_attachments");
          for (const a of input.attachments) {
            attachments.add(newAttachmentRow(a, { clientSetId: input.clientSetId }));
          }
        },
      );
    },

    async listChangeRequestSetRows() {
      const rows = await getAllRows<ChangeRequestSetRow>(readStore("outbox_change_request_sets"));
      rows.sort((x, y) => compareStrings(x.client_set_id, y.client_set_id));
      return rows.map(toChangeRequestSetRow);
    },

    async listAttachmentsForChangeRequestSet(clientSetId) {
      const rows = await getAllRows<AttachmentRow>(
        readStore("outbox_attachments").index("client_set_id"),
        clientSetId,
      );
      rows.sort((x, y) => compareStrings(x.client_attachment_id, y.client_attachment_id));
      return rows.map(toAttachmentRow);
    },

    async answerIntakeCandidates(clientSetId, answer, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "needs_answer") return false;
        const body = parseBody(row);
        if (answer.kind === "pick") {
          body.anchor_kind = "standalone";
          body.facility_id = answer.facilityId;
        } else {
          body.dismissed_candidates = (row.candidates ?? []).map((c) => ({
            facility_id: c.facility_id,
            reason: answer.reason,
          }));
          body.candidates_checked_at = new Date(now).toISOString();
        }
        row.body_json = JSON.stringify(body);
        row.state = "queued";
        row.candidates = null;
        row.updated_at = now;
        return true;
      });
    },

    async retryBlockedChangeRequestSet(clientSetId, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "blocked") return false;
        row.state = "queued";
        row.evidence_failures = null;
        row.updated_at = now;
        return true;
      });
    },

    async amendBlockedChangeRequestSet(clientSetId, body, addedAttachments, now = Date.now()) {
      await runWrite(
        db,
        ["outbox_change_request_sets", "outbox_attachments", "evidence"],
        "enqueue",
        async (tx) => {
          const sets = tx.objectStore("outbox_change_request_sets");
          const row = await getRow<ChangeRequestSetRow>(sets, clientSetId);
          if (row === null || row.state !== "blocked") return;
          await assertEvidenceStaged(tx, addedAttachments);
          row.body_json = JSON.stringify(body);
          row.state = "queued";
          row.evidence_failures = null;
          row.updated_at = now;
          sets.put(row);
          const attachments = tx.objectStore("outbox_attachments");
          for (const a of addedAttachments) {
            attachments.add(newAttachmentRow(a, { clientSetId }));
          }
        },
      );
    },

    async reopenChangeRequestSet(clientSetId) {
      const tx = db.transaction(
        ["outbox_change_request_sets", "outbox_attachments", "draft_attachments"],
        "readwrite",
      );
      const sets = tx.objectStore("outbox_change_request_sets");
      const row = await getRow<ChangeRequestSetRow>(sets, clientSetId);
      if (row !== null && row.state === "error" && row.receipt === null) {
        const attachments = tx.objectStore("outbox_attachments");
        const drafts = tx.objectStore("draft_attachments");
        const files = await getAllRows<AttachmentRow>(
          attachments.index("client_set_id"),
          clientSetId,
        );
        for (const file of files) {
          drafts.put({
            client_attachment_id: file.client_attachment_id,
            assignment_id: file.request?.stagedTestClientSubmissionId ?? clientSetId,
            link_id: file.link_id,
            kind: file.kind,
            file_uri: file.file_uri,
            content_type: file.content_type,
            file_name: file.file_name,
            staged_at: file.staged_at,
          });
          attachments.delete(file.client_attachment_id);
        }
        sets.delete(clientSetId);
      }
      await txDone(tx);
    },

    async markChangeRequestSetNeedsAnswer(clientSetId, candidates, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "queued") return false;
        row.state = "needs_answer";
        row.candidates = candidates;
        row.last_check_failed_at = null;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetChecked(clientSetId, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "queued") return false;
        const body = parseBody(row);
        body.candidates_checked_at = new Date(now).toISOString();
        row.body_json = JSON.stringify(body);
        row.last_check_failed_at = null;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetCheckFailed(clientSetId, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "queued") return false;
        row.last_check_failed_at = now;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetSending(clientSetId, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "queued") return false;
        row.state = "sending";
        row.updated_at = now;
        return true;
      });
    },

    async storeChangeRequestSetReceipt(clientSetId, receipt, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        if (row.state !== "sending") return false;
        row.receipt = receipt;
        row.state = receipt.status === "awaiting_evidence" ? "uploading_evidence" : "received";
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetAttachmentUploaded(
      clientAttachmentId,
      setAttachmentsComplete,
      now = Date.now(),
    ) {
      const tx = db.transaction(["outbox_attachments", "outbox_change_request_sets"], "readwrite");
      const attachments = tx.objectStore("outbox_attachments");
      const attachment = await getRow<AttachmentRow>(attachments, clientAttachmentId);
      if (attachment !== null) {
        attachment.state = "uploaded";
        attachments.put(attachment);
        if (setAttachmentsComplete && attachment.client_set_id !== null) {
          const sets = tx.objectStore("outbox_change_request_sets");
          const parent = await getRow<ChangeRequestSetRow>(sets, attachment.client_set_id);
          if (parent !== null && parent.state === "uploading_evidence") {
            parent.state = "received";
            parent.updated_at = now;
            sets.put(parent);
          }
        }
      }
      await txDone(tx);
    },

    async markChangeRequestSetBlocked(clientSetId, blockedRequirements, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        row.state = "blocked";
        row.evidence_failures = blockedRequirements;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetError(clientSetId, lastError, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        row.state = "error";
        row.last_error = lastError;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetRetry(clientSetId, lastError, now = Date.now()) {
      await updateSetRow(clientSetId, (row) => {
        row.state = "queued";
        row.attempts += 1;
        row.last_error = lastError;
        row.updated_at = now;
        return true;
      });
    },

    async markChangeRequestSetSynced(clientSetId, now = Date.now()) {
      const tx = db.transaction(
        ["outbox_change_request_sets", "draft_sidecars", "draft_attachments", "evidence"],
        "readwrite",
      );
      const sets = tx.objectStore("outbox_change_request_sets");
      const row = await getRow<ChangeRequestSetRow>(sets, clientSetId);
      if (row !== null && row.state === "received") {
        row.state = "synced";
        row.updated_at = now;
        sets.put(row);
        const sidecars = tx.objectStore("draft_sidecars");
        const sidecar = await getRow<{ key: string; sidecar_json: string }>(sidecars, clientSetId);
        if (sidecar !== null) {
          // sidecar_json was written as JSON.stringify of a DraftSidecar.
          const owners = sidecarEvidenceOwners(JSON.parse(sidecar.sidecar_json) as DraftSidecar);
          for (const owner of owners) {
            await deleteByOwner(tx.objectStore("draft_attachments"), owner);
            await deleteByOwner(tx.objectStore("evidence"), owner);
          }
          sidecars.delete(clientSetId);
        }
      }
      await txDone(tx);
    },
  };
}
